// SecondTourResultPopover.tsx
import React, { useEffect, useState } from 'react';
import { Popover, Button, InputNumber, Space, message, App, Tag } from 'antd';
import { EditOutlined, CheckOutlined } from '@ant-design/icons';
import { Participant } from './TeacherOlympiadList';
import { postRequest } from '../../../../api/httpRequest';

interface SecondTourResultPopoverProps {
    participant: Participant;
    currentResult: string;
    passingScore: number;
    maxScore: number;
    olympiadId: number;
    onResultUpdate?: () => void;
}

const SecondTourResultPopover: React.FC<SecondTourResultPopoverProps> = ({
    participant,
    currentResult,
    passingScore,
    maxScore,
    olympiadId,
    onResultUpdate
}) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const initialResult = currentResult != null ? parseInt(currentResult) : null;
    const [result, setResult] = useState<number | null>(initialResult);
    
    const { notification } = App.useApp();

    const handleSaveResult = async () => {
        if (result === null) {
            message.warning('Введите результат');
            return;
        }

        setLoading(true);
        try {
            await postRequest('/server/olympiad/teacher/secondTour', {
                participantId: participant.id,
                olympiadId: olympiadId,
                result: result
            });

            message.success('Результат сохранен');
            setVisible(false);
            
            if (onResultUpdate) {
                onResultUpdate();
            }
        } catch (error) {
            console.error('Ошибка при сохранении результата:', error);
            notification.error({
                message: 'Ошибка сохранения',
                description: 'Не удалось сохранить результат'
            });
        } finally {
            setLoading(false);
        }
    };

    const isAllowedToEdit = participant.firstTourPass === true;

    const content = (
        <div style={{ width: 250 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                    <strong>Участник:</strong> {participant.fio}
                </div>
                <div>
                    <strong>Допуск к второму туру:</strong> 
                    <Tag color={participant.firstTourPass ? 'green' : 'red'} style={{ marginLeft: 8 }}>
                        {participant.firstTourPass ? 'Допущен' : 'Не допущен'}
                    </Tag>
                </div>
                <div>
                    <strong>Первый тур:</strong> {participant.firstTourOption1} баллов
                </div>
                <div>
                    <strong>Максимальный балл:</strong> {maxScore}
                </div>
                <InputNumber
                    min={0}
                    max={maxScore}
                    placeholder="Введите результат"
                    value={result}
                    onChange={setResult}
                    style={{ width: '100%' }}
                    disabled={!isAllowedToEdit}
                />
                <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleSaveResult}
                    loading={loading}
                    disabled={!isAllowedToEdit}
                    style={{ width: '100%' }}
                >
                    Сохранить результат
                </Button>
                {!isAllowedToEdit && (
                    <div style={{ color: '#ff4d4f', fontSize: '12px', textAlign: 'center' }}>
                        Участник не допущен ко второму туру
                    </div>
                )}
            </Space>
        </div>
    );

    const displayResult = currentResult != null ? 
        `Редактировать` : 'Ввести результат';

    return (
        <Popover
            content={content}
            title="Результат второго тура"
            trigger="click"
            open={visible}
            onOpenChange={setVisible}
            placement="left"
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {currentResult != null && (
                    <Tag color="blue" style={{ margin: 0 }}>
                        {currentResult}
                    </Tag>
                )}
                <Button 
                    type="link" 
                    icon={<EditOutlined />}
                    disabled={!isAllowedToEdit}
                    size="small"
                >
                    {isAllowedToEdit ? displayResult : 'Не допущен'}
                </Button>
            </div>
        </Popover>
    );
};

export default SecondTourResultPopover;